import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, VideoOff } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const CameraPlaceholder = ({ icon: Icon, title, description }) => (
  <div className="w-full h-full bg-gray-900 flex flex-col items-center justify-center px-3 text-center">
    <Icon className="h-8 w-8 text-gray-600 mb-2" />
    <p className="text-gray-300 text-sm font-medium">{title}</p>
    {description && <p className="text-gray-500 text-xs mt-1">{description}</p>}
  </div>
);

export default function CameraImage({ camera, isPlaying, onError, cameraIndex = 0 }) {
  const [imageUrl, setImageUrl] = useState('');
  const [failure, setFailure] = useState(null);
  const [snapshotFallback, setSnapshotFallback] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const token = localStorage.getItem('token');
  const snapshotUrl = camera.streamUrls?.proxySnapshotUrl;
  const mjpegUrl = camera.streamUrls?.proxyMjpegUrl;
  const unavailable = camera.enabled === false;

  useEffect(() => { setSnapshotFallback(false); }, [camera.id, mjpegUrl, isPlaying]);

  useEffect(() => {
    let active = true;
    let timer;
    let blobUrl;
    const controller = new AbortController();
    setImageUrl('');
    setFailure(null);
    const withToken = (url) => {
      const base = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';
      const full = url.startsWith('http') ? url : `${base}${url}`;
      return `${full}${full.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}&_t=${Date.now()}`;
    };
    const loadSnapshot = async () => {
      try {
        const response = await fetch(withToken(snapshotUrl), {
          headers: { Accept: 'image/*', 'Cache-Control': 'no-cache' },
          credentials: 'include', cache: 'no-store', signal: controller.signal
        });
        if (!response.ok) {
          const details = await response.json().catch(() => ({}));
          throw new Error(details.message || `Błąd ${response.status}`);
        }
        const blob = await response.blob();
        if (!active) return;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
        setFailure(null);
      } catch (error) {
        if (!active) return;
        setImageUrl('');
        setFailure(error.message || 'Brak połączenia z serwerem');
        onErrorRef.current?.(error);
      } finally {
        // Następna klatka dopiero po zakończeniu poprzedniej: brak kolejki
        // nakładających się żądań, również przy wolnym rejestratorze.
        if (active) timer = setTimeout(loadSnapshot, 2000);
      }
    };

    if (!unavailable) {
      if (isPlaying && mjpegUrl && !snapshotFallback) {
        setImageUrl(withToken(mjpegUrl));
      } else if (snapshotUrl) {
        timer = setTimeout(loadSnapshot, cameraIndex * 120);
      } else {
        setFailure('Brak adresu podglądu tej kamery');
      }
    }
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [camera.id, snapshotUrl, mjpegUrl, unavailable, isPlaying, snapshotFallback, cameraIndex, token]);

  if (unavailable) {
    return <CameraPlaceholder icon={VideoOff} title="Brak strumienia kamery" description={camera.offlineReason} />;
  }
  if (failure) return <CameraPlaceholder icon={AlertTriangle} title="Brak obrazu" description={failure} />;
  if (!imageUrl) return <div className="w-full h-full bg-gray-900 flex items-center justify-center"><LoadingSpinner /></div>;
  return <img src={imageUrl} alt={camera.name} className="w-full h-full object-cover" onError={() => {
    setImageUrl('');
    if (isPlaying && mjpegUrl && !snapshotFallback) setSnapshotFallback(true);
    else setFailure('Nie udało się wyświetlić obrazu');
  }} />;
}
